import Vue from 'vue';
import VueRouter from 'vue-router';
import axiosInstance from '../services/axiosInstance';
import { CancelToken } from 'axios';
import routes from './routes';

Vue.use(VueRouter);

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

// Create a CancelToken source
const source = CancelToken.source();

export default function ({ store } /* { store, ssrContext } */) {
  const Router = new VueRouter({
    scrollBehavior: () => ({
      x: 0,
      y: 0,
    }),
    routes,
    // Leave these as they are and change in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    mode: process.env.VUE_ROUTER_MODE,
    base: process.env.VUE_ROUTER_BASE,
  });

  // Add a response interceptor to handle 401 and 403 errors
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (
        error.response &&
        (error.response.status === 403 || error.response.status === 401)
      ) {
        // Remove the token from session storage
        sessionStorage.removeItem('token');
        // Clear the current user
        store.dispatch('auth/setCurrentUser', null);
        // If the user is not on the login page
        if (Router.currentRoute.name !== 'login') {
          // Show a notification to the user
          Vue.prototype.$q.notify({
            message: 'Your session has expired.',
            color: 'negative',
            position: 'top',
            timeout: 3000,
          });
          // Redirect the user to the login page
          Router.push('/login');
        }
      }
      // Cancel any pending requests - this will not cancel any in-flight requests
      source.cancel();
      // Reject the error
      return Promise.reject(error);
    }
  );

  Router.beforeEach(async (to, from, next) => {
    // If the route requires authentication
    if (to.matched.some((record) => record.meta.requiresAuth)) {
      // If route contains meta property forceUserCheck, send a request to User service to verify this user is valid -- This is intended as a way to force logout a user who has an expired token and/or has been removed from the user DB when clicking on a tab that has 'pre-fetched' data (Documents, Leaders, and Trends). Navigating to those tabs after fetching data previously won't force a re-fetch so they could navigate between them without validating the auth token
      if (to.meta.forceUserCheck) {
        // Dispatch action to user service to check that the current user exists -- If not, user service will return a 403 error which will invalidate the current auth token
        if (sessionStorage.getItem('user')) {
          const user = JSON.parse(sessionStorage.getItem('user'));
          const currentUserRes = await store.dispatch(
            'users/getUser',
            user.uid
          );
          if (!currentUserRes.success) {
            // Request to user service failed, do not allow navigation to continue -- Send user to login screen
            // Throw console error with message from user service
            console.error(currentUserRes.message);
            store.dispatch('auth/setCurrentUser', null);
            next('/login');
          }
        } else {
          // No user found in session storage, send to login page
          next('/login');
        }
      }

      // If token in session, allow navigation
      if (sessionStorage.getItem('token')) {
        next();
        return;
      }
      // If no token, clear the current user and redirect to login
      store.dispatch('auth/setCurrentUser', null);
      next('/login');
    } else {
      next();
    }
  });

  Router.afterEach((to, from, failure) => {
    updateStore(to);
  });

  function updateStore(to) {
    // Update the active data source in app module each time navigation initiated
    const dataSource = to.meta.dataSource || null;
    store.dispatch('app/changeActiveDataSource', dataSource);

    const routeName = to.name;
    if (routeName) {
      // Set the active route in app module each time navigation initiated
      store.dispatch('app/setActiveRoute', routeName);
      // Update the active view in the app module each time navigation initiated
      let tab = null;
      let activeView = null;
      // Check if the route name contains a '.' -- route structure is 'tab.view' for main tabs
      if (to.name.includes('.')) {
        // Get the main tab, the name before the '.' but remove the 's' at the end as we use the singular form in state
        tab = to.name.split('.')[0].replace(/s$/, '');
        // Get the active view, the name after the '.'
        activeView = to.name.split('.')[1];
      }
      // Set the active view in the app module
      store.dispatch('app/setActiveView', { tab, activeView });

      // Update the help content in help module for the current route
      store.dispatch(
        'help/setHelpContent',
        activeView ? activeView : routeName
      );
    }
  }

  return Router;
}
